import {
  Form,
  IconAttention,
  PrimaryButton,
  Space,
  TertiaryButton,
  WarningAlert,
  WithFormControlSelect,
} from '@procurify/ui'
import { type FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { CurrencySelect } from '@webapp/core/components/CurrencySelect'
import { type Currency } from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { ExternalPaymentMethodFormFieldsStyled } from './styles/ExternalPaymentMethodFormFieldsStyled'
import { ExternalPaymentMethodFormFooterStyled } from './styles/ExternalPaymentMethodFormFooterStyled'
import { ExternalPaymentMethodFormStyled } from './styles/ExternalPaymentMethodFormStyled'

export interface ExternalPaymentMethodFormSchema {
  name: string
  currency: Currency
  description: string
}
export interface IExternalPaymentMethodFormProps {
  initialValues?: Partial<ExternalPaymentMethodFormSchema>
  onSubmit: (
    data: ExternalPaymentMethodFormSchema,
    formikHelpers: FormikHelpers<ExternalPaymentMethodFormSchema>
  ) => void
  onCancel: () => void
  shouldShowCancelButtonLabel?: boolean
}

export const ExternalPaymentMethodForm = ({
  initialValues,
  onSubmit,
  onCancel,
  shouldShowCancelButtonLabel = false,
}: IExternalPaymentMethodFormProps) => {
  const intl = useProcurifyIntl()

  const initialFormValues: ExternalPaymentMethodFormSchema = {
    name: '',
    currency: null,
    description: '',
    ...initialValues,
  }

  const validationSchema = Yup.object<ExternalPaymentMethodFormSchema>({
    name: Yup.string().required(
      intl.formatMessage(messages.fieldRequiredMessage)
    ),
    currency: Yup.object<Currency>()
      .required(intl.formatMessage(messages.fieldRequiredMessage))
      .nullable(),
    description: Yup.string().required(
      intl.formatMessage(messages.fieldRequiredMessage)
    ),
  })

  return (
    <Form<ExternalPaymentMethodFormSchema>
      initialValues={initialFormValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form.Content noValidate autoComplete='off' style={{ height: '100%' }}>
          <ExternalPaymentMethodFormStyled>
            <ExternalPaymentMethodFormFieldsStyled>
              <Space>
                <WarningAlert icon={<IconAttention block color='warning' />}>
                  {intl.formatMessage(messages.warningAlertText)}
                </WarningAlert>

                <WithFormControlSelect
                  id='currency'
                  name='currency'
                  label={intl.formatMessage(messages.currencyFieldLabel)}
                  required
                >
                  <CurrencySelect />
                </WithFormControlSelect>

                <Form.TextField
                  id='name'
                  name='name'
                  label={intl.formatMessage(messages.nameFieldLabel)}
                  required
                />

                <Form.TextField
                  id='description'
                  name='description'
                  required
                  label={intl.formatMessage(messages.descriptionFieldLabel)}
                  multiline
                  minRows={3}
                />
              </Space>
            </ExternalPaymentMethodFormFieldsStyled>

            <ExternalPaymentMethodFormFooterStyled>
              <Space direction='vertical' justify='center' align='stretch'>
                <PrimaryButton type='submit' disabled={isSubmitting} fullWidth>
                  {intl.formatMessage(messages.submitButtonLabel)}
                </PrimaryButton>
                <TertiaryButton
                  type='button'
                  onClick={onCancel}
                  disabled={isSubmitting}
                  fullWidth
                >
                  {intl.formatMessage(
                    shouldShowCancelButtonLabel
                      ? messages.cancelButton
                      : messages.backButton
                  )}
                </TertiaryButton>
              </Space>
            </ExternalPaymentMethodFormFooterStyled>
          </ExternalPaymentMethodFormStyled>
        </Form.Content>
      )}
    </Form>
  )
}
